import { ApiRequestError, EmptyState, Skeleton, Tag, readerTimezone, useErrorToast } from '@h2hspark/ui';
import { CalendarCheck, Check, ChevronRight, UserMinus, UserX } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageBackLink } from '@/member/page-back-link';

import { eventDetailPath } from '../event-discovery/constants';
import { ConnectionMessagesPanel } from '../messages/messages-panel';

import { useConnection, useRemoveConnection } from './api';
import {
  ABOUT_LABEL,
  CONNECTIONS_PATH,
  DISCONNECT_FAILED,
  DISCONNECT_HELPER,
  DISCONNECT_LABEL,
  CONNECTED_FROM_A_PROFILE_CODE,
  NO_EVENT_ON_THIS_CONNECTION,
  NOTHING_WRITTEN_ABOUT_THEM,
  WHERE_YOU_MET_LABEL,
  connectedOnLine,
  connectedPillLabel,
  initialsOf,
  personName,
  roleAtCompany,
  whereYouMetRowAccessibleName,
} from './constants';
import { DisconnectDialog } from './disconnect-dialog';
import { PrivateNotePanel } from './private-note-panel';
import { SocialChannelsPanel } from './social-channels-panel';

/**
 * The connection detail (`MEM-033` AC-2, AC-3, AC-5, AC-6; `MEM-034` AC-1).
 *
 * ## Transcribed from `Web App.dc.html` (10 September), `showPerson`, §2.11
 *
 * A back link; then the header — a 74px avatar, the name at `var(--text-4xl)` over the role at
 * `var(--text-lg)` in `--text-muted`, and the *"Connected · 1 event together"* pill in the success
 * colours; then a `minmax(0,1.35fr) minmax(0,1fr)` grid, `align-items:start`, at an 18px gap:
 *
 *   - **left** — the private note card;
 *   - **right** — *About*, *Elsewhere*, and *Where you met*.
 *
 * Below `lg` the two columns stack in that order (`MEM-056` AC-2's reference table: *one column,
 * messages first* — messages is the left column, and the note is what the left column holds until
 * Phase 6). `lg` rather than `md` for the same measured reason the event page's sidebar opens
 * there: at 768px the 1.35/1 split gives the right column 300px, which is narrower than a social
 * channel's own address.
 *
 * ## What replaced the screen before it
 *
 * A full-bleed `--spark-gradient` header with a centred 76px tile, then a single column of
 * sections — the phone prototype's `CONNECTION DETAIL + NOTES`, stretched to the desktop canvas.
 * Sumit's ruling of 2026-09-11 is one app with a responsive layout, and `Web App.dc.html` is the
 * design at every width.
 *
 * ## §2.11's Messages card, built — at the top of the left column, above the note
 *
 * `MEM-054` AC-2, under ADR 052. The slot this page has been holding open since Phase 4 now holds
 * `ConnectionMessagesPanel`: the prototype's header, its bubbles, its empty state and its
 * composer. The card is headerless inside, because the page above already names the person at
 * `text-4xl` and a second name on one screen is a name twice.
 *
 * It is drawn for a **removed** connection too — that page does not resolve, so in practice this
 * is the live case — and the panel decides for itself whether a composer belongs there
 * (`MEM-054` AC-5, AC-7): a closed thread gets its sentence instead.
 *
 * ## About carries the quote, the bio and the interests — since 2026-09-12
 *
 * §2.11 draws a quote, a paragraph and interest pills, and this card drew a sentence saying the
 * screen did not have them. Sumit read that on a real connection: *"why are we not showing the
 * about and their interests here like in the prototype?"*
 *
 * It was a wiring gap rather than a decision. `MEM-007` shipped the bio and the storage had it;
 * the conversation starter and the interests were already served to a **co-registrant** by the
 * attendee profile (`MEM-012` AC-1); and no port between the profile row and this screen had been
 * widened to carry any of the three to the one reader with the strongest claim to them. The API
 * now fetches them through `describeConnectedProfile`, a **detail-only** read taken past the
 * active-row check, so the connections list and the inbound-requests screen still cannot see them.
 *
 * Each of the three is drawn only when the member wrote it, and the empty state is about **them**
 * rather than about our plumbing. `MEM-033` AC-2 is reworded to say so.
 *
 * ## Where you met has one row, because a connection has one event
 *
 * §2.11 lists every event two people have shared, from a fixture that gives some people several.
 * A connection in this build originates at exactly one event (`MEM-033` AC-6), so there is one row
 * and the pill above it says *"1 event together"*. The row's second line is **when the two of you
 * connected** rather than the prototype's `{date} · {city}`: the connection payload resolves an
 * event to its name and nothing else, and the row links to the event, where the date and the city
 * both are. `MEM-057`'s profile connect code makes a connection with no event at all, and the
 * card then says so rather than rendering an empty list.
 *
 * ## Disconnect stays where it was (`MEM-034` AC-1)
 *
 * At the foot of the right column, behind the same confirmation, with the same helper line — see
 * `disconnect-dialog.tsx` for why that line departs from the prototype's *"clears your notes"*.
 */
export function ConnectionDetailPage(): React.ReactElement {
  const { connectionRef } = useParams<{ connectionRef: string }>();
  const navigate = useNavigate();
  const query = useConnection(connectionRef ?? null, { enabled: true });
  const remove = useRemoveConnection(connectionRef ?? '');
  const showError = useErrorToast();
  const [confirming, setConfirming] = React.useState(false);
  const reader = React.useMemo(() => readerTimezone(), []);

  React.useEffect(() => {
    if (remove.error !== null) showError(remove.error, DISCONNECT_FAILED);
  }, [remove.error, showError]);

  if (query.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <div className="grid gap-[18px] lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
          <Skeleton className="h-[260px] w-full rounded-xl" />
          <Skeleton className="h-[320px] w-full rounded-xl" />
        </div>
      </div>
    );
  }

  const notFound = query.error instanceof ApiRequestError && query.error.statusCode === 404;
  const connection = query.data?.connection;
  if (notFound || connection === undefined) {
    // One answer for a reference that names nobody, a connection that was removed, and a
    // reference that was invented — the API makes no distinction, and neither does this.
    return (
      <div className="space-y-6">
        <BackLink />
        <EmptyState
          icon={<UserX className="h-6 w-6" aria-hidden />}
          title="That connection is not on your list"
          description="It may have been removed. Your connections are on the list behind you."
        />
      </div>
    );
  }

  const name = personName(connection);
  const line = roleAtCompany(connection);
  const connectedOn = connectedOnLine(connection.establishedAt, reader);

  return (
    <div>
      <BackLink />

      {/* §2.11's header: the avatar, the name over the role, then the state pill. */}
      <header className="mb-[26px] mt-[18px] flex flex-wrap items-center gap-[18px]">
        <span
          className="inline-flex h-[74px] w-[74px] shrink-0 items-center justify-center rounded-pill bg-brand-100 font-brand text-2xl font-extrabold text-brand-700"
          aria-hidden
        >
          {initialsOf(connection)}
        </span>
        <div className="min-w-0 flex-1 basis-48">
          <h1 className="mb-1 text-2xl tracking-tight md:text-4xl">{name}</h1>
          {line === null ? null : <p className="text-md text-muted-foreground md:text-lg">{line}</p>}
        </div>
        {/* `MEM-036`'s follow-up badge comes first where one applies — AC-2's order — and nothing
            in this slice writes the state, so today it is always absent rather than invented. */}
        <div className="flex flex-none flex-wrap gap-2.5">
          {connection.followUpNotice === null ? null : (
            <span className="inline-flex items-center rounded-pill bg-success-subtle px-4 py-2 font-brand text-sm font-bold text-success">
              {connection.followUpNotice}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 rounded-pill bg-success-subtle px-4 py-2 font-brand text-sm font-bold text-success">
            <Check className="h-[15px] w-[15px]" aria-hidden />
            {connectedPillLabel(connection.metAtNotice === null ? 0 : 1)}
          </span>
        </div>
      </header>

      <div className="grid items-start gap-[18px] lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-[18px]">
          {/* §2.11's left column, top: the Messages card (`MEM-054` AC-2). */}
          <ConnectionMessagesPanel connectionRef={connection.connectionRef} />

          <section className="rounded-xl border border-border-subtle bg-card p-[22px] shadow-sm">
            <PrivateNotePanel connection={connection} />
          </section>
        </div>

        <div className="flex flex-col gap-[18px]">
          {/*
            §2.11's About card: the quote, the paragraph, the pills — built 2026-09-12.

            Each of the three renders only when the member wrote it, and the sentence below appears
            only when none of them did. A card that drew an empty quote or a `—` where a bio goes
            would be inventing a silence the member did not choose.
          */}
          <section className="rounded-xl border border-border-subtle bg-card p-[22px] shadow-sm">
            <h2 className="mb-3 text-lg tracking-tight">{ABOUT_LABEL}</h2>
            {connection.conversationStarter === null ? null : (
              // §2.11's quote: `font-brand` 700 `--text-md` in `--brand-primary`, in curly
              // quotation marks, above the paragraph. Their own words, so they are quoted.
              <p className="mb-2.5 font-brand text-md font-bold text-primary">
                “{connection.conversationStarter}”
              </p>
            )}
            {connection.bio === null ? null : (
              <p className="mb-4 whitespace-pre-line text-md leading-relaxed text-text-body">
                {connection.bio}
              </p>
            )}
            {connection.interests.length === 0 ? null : (
              <ul className="flex flex-wrap gap-2">
                {connection.interests.map((interest) => (
                  <li key={interest.termId}>
                    {/* §2.11's pill — `--purple-50` on `--brand-primary`, `font-brand` 700, 7px/14px
                        — as a className on the shared `Tag` rather than a second chip component. */}
                    <Tag className="border-transparent bg-brand-50 px-3.5 py-[7px] font-brand font-bold text-primary">
                      {interest.displayName}
                    </Tag>
                  </li>
                ))}
              </ul>
            )}
            {connection.conversationStarter === null &&
            connection.bio === null &&
            connection.interests.length === 0 ? (
              <p className="text-md leading-relaxed text-muted-foreground">
                {NOTHING_WRITTEN_ABOUT_THEM}
              </p>
            ) : null}
          </section>

          {/* `MEM-033` AC-5. The one place in the product these render. */}
          <section className="rounded-xl border border-border-subtle bg-card p-[22px] shadow-sm">
            <SocialChannelsPanel channels={connection.socialChannels} />
          </section>

          <section className="rounded-xl border border-border-subtle bg-card p-[22px] shadow-sm">
            <h2 className="mb-1 text-lg">{WHERE_YOU_MET_LABEL}</h2>
            <p className="mb-3.5 text-sm text-muted-foreground">
              {connectedPillLabel(connection.metAtNotice === null ? 0 : 1)}
            </p>
            {connection.eventId === null ? (
              // Never had one (`MEM-057` AC-4). A different sentence from the one below, because
              // "the event has gone" and "there was no event" are different facts and only the
              // second one explains why no follow-up opener will arrive for this person.
              <p className="rounded-md bg-ivory-100 px-3.5 py-[13px] text-sm text-muted-foreground">
                {CONNECTED_FROM_A_PROFILE_CODE}
              </p>
            ) : connection.eventName === null ? (
              <p className="rounded-md bg-ivory-100 px-3.5 py-[13px] text-sm text-muted-foreground">
                {NO_EVENT_ON_THIS_CONNECTION}
              </p>
            ) : (
              <Link
                to={eventDetailPath(connection.eventId)}
                aria-label={whereYouMetRowAccessibleName(connection.eventName)}
                className="flex items-center gap-3.5 rounded-md bg-ivory-100 px-3.5 py-[13px] transition-colors hover:bg-brand-50"
              >
                <CalendarCheck className="h-[17px] w-[17px] shrink-0 text-primary" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-brand text-md font-bold text-foreground">
                    {connection.eventName}
                  </span>
                  {connectedOn === null ? null : (
                    <span className="block text-sm text-muted-foreground">{connectedOn}</span>
                  )}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-text-subtle" aria-hidden />
              </Link>
            )}
          </section>

          {/* `MEM-034` AC-1 — removal, from the connected member's own screen. */}
          <section className="border-t border-border-subtle pt-4">
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => {
                setConfirming(true);
              }}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-pill border-[1.5px] border-border bg-card p-3 font-brand text-sm font-bold text-destructive disabled:opacity-60"
            >
              <UserMinus className="h-4 w-4" aria-hidden />
              {DISCONNECT_LABEL}
            </button>
            <p className="mt-2 px-0.5 text-xs leading-snug text-text-subtle">{DISCONNECT_HELPER}</p>
          </section>
        </div>
      </div>

      <DisconnectDialog
        open={confirming}
        firstName={connection.firstName}
        busy={remove.isPending}
        onOpenChange={setConfirming}
        onConfirm={() => {
          remove.mutate(undefined, {
            onSuccess: () => {
              setConfirming(false);
              // Back to a list that no longer contains them, which says "done" without a second
              // sheet to dismiss.
              void navigate(CONNECTIONS_PATH);
            },
          });
        }}
      />
    </div>
  );
}

function BackLink(): React.ReactElement {
  return (
    <PageBackLink to={CONNECTIONS_PATH}>
      Connections
    </PageBackLink>
  );
}
